import * as THREE from 'three';
import type { Universe } from '../astro/Universe';
import type { PlayerShip } from './PlayerShip';

export type TargetRef = { kind: 'body'; id: string } | { kind: 'player'; id: string };

export type TargetInfo = {
  ref: TargetRef;
  name: string;
  position: THREE.Vector3; // heliocentric
  radius: number; // 0 for ships
  color: string;
};

const tmp = new THREE.Vector3();
const tmp2 = new THREE.Vector3();
const desiredQ = new THREE.Quaternion();
const m = new THREE.Matrix4();

/**
 * Flies the ship to a stop point near the target: turn, cruise, brake.
 * Steers around planets/sun that sit on the straight path.
 */
export class Autopilot {
  engaged = false;
  arrived = false;
  /** distance to the stop point */
  remaining = Infinity;

  stopDistance(t: TargetInfo): number {
    return t.radius > 0 ? t.radius * 3 : 700;
  }

  update(dt: number, ship: PlayerShip, target: TargetInfo, universe: Universe): void {
    if (!this.engaged) return;
    ship.autopilotControl = true;
    const stop = this.stopDistance(target);
    const toTarget = tmp.subVectors(target.position, ship.position);
    const dist = toTarget.length();
    // Stop point sits on the near side of the target.
    const stopPoint = tmp2.copy(target.position).addScaledVector(toTarget, -stop / Math.max(dist, 1));
    let aim = stopPoint.clone();
    this.remaining = Math.max(0, dist - stop);

    // Obstacle avoidance: the first body (other than the target) whose padded sphere crosses the path.
    const dir = aim.clone().sub(ship.position);
    const len = dir.length();
    dir.divideScalar(len);
    for (const b of universe.bodies) {
      if (target.ref.kind === 'body' && b.def.id === target.ref.id) continue;
      const pad = b.def.radius * (b.def.kind === 'star' ? 4 : 2.2);
      const oc = b.position.clone().sub(ship.position);
      const t = oc.dot(dir);
      if (t <= 0 || t > len) continue;
      const closest = ship.position.clone().addScaledVector(dir, t);
      const miss = closest.distanceTo(b.position);
      if (miss < pad) {
        const side = closest.sub(b.position);
        if (side.lengthSq() < 1) side.set(0, 0, 1).cross(dir);
        aim = b.position.clone().addScaledVector(side.normalize(), pad * 1.3);
        break;
      }
    }

    // Turn towards the aim point.
    const aimDir = aim.sub(ship.position).normalize();
    m.lookAt(new THREE.Vector3(), aimDir, ship.up());
    desiredQ.setFromRotationMatrix(m);
    const angle = ship.quaternion.angleTo(desiredQ);
    const maxStep = 1.4 * dt;
    ship.quaternion.rotateTowards(desiredQ, maxStep);
    ship.angularVelocity.set(0, 0, 0);

    // Throttle: full when aligned, brake near the stop point.
    const align = Math.max(0, Math.cos(angle));
    const brake = Math.min(1, (this.remaining * 0.5) / Math.max(ship.maxSpeed, 1));
    ship.throttle = angle > 0.6 ? 0.02 : Math.min(align * align, brake);

    // Following a player: never "arrive", keep station.
    if (target.ref.kind === 'player') {
      if (this.remaining < 30) ship.throttle = 0;
      return;
    }
    if (this.remaining < Math.max(50, stop * 0.03) && ship.speed < Math.max(30, stop * 0.02)) {
      this.arrived = true;
      this.disengage(ship);
    }
  }

  engage(): void {
    this.engaged = true;
    this.arrived = false;
  }

  disengage(ship: PlayerShip): void {
    this.engaged = false;
    ship.autopilotControl = false;
    ship.throttle = 0;
  }
}
